import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CreditCard, CalendarPlus, Package, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageLoading, EmptyState, PageHeader } from "@/components/shared";
import api, { formatApiErrorDetail } from "@/lib/api";

const STATUS_STYLE = {
  active: "bg-emerald-50 text-emerald-700",
  trialing: "bg-cyan/10 text-cyan-dark",
  none: "bg-slate-100 text-slate-500",
};

export default function AdminSubscriptions() {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [stripe, setStripe] = useState(null);
  const [target, setTarget] = useState(null);
  const [days, setDays] = useState(14);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/subscriptions").then((r) => setRows(r.data.subscriptions));
  useEffect(() => { load(); api.get("/admin/stripe/products").then((r) => setStripe(r.data)).catch(() => setStripe({ products: [], stripe_mode: "unknown" })); }, []);
  if (rows === null) return <PageLoading />;
  const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");

  const extend = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/companies/${target.id}/extend-trial`, { days: Number(days) || 14 });
      toast.success(t("x.extended"));
      setTarget(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-subscriptions">
      <PageHeader title="Subscriptions" subtitle="Billing status across all companies." />
      {rows.length === 0 ? <EmptyState icon={CreditCard} title="No companies yet" /> : (
        <div className="mt-6 card-soft overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-400">
              <th className="px-4 py-3">Company</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Billing</th><th className="px-4 py-3">Renews / Trial ends</th><th className="px-4 py-3">{t("x.actions")}</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-50" data-testid={`sub-row-${r.id}`}>
                  <td className="px-4 py-3 font-semibold text-navy">{r.name}</td>
                  <td className="px-4 py-3 capitalize">{r.tier}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[r.status] || STATUS_STYLE.none}`}>{r.status}</span></td>
                  <td className="px-4 py-3 text-slate-500">{r.interval || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{fmt(r.current_period_end || r.trial_ends_at)}</td>
                  <td className="px-4 py-3">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setTarget(r); setDays(14); }} data-testid={`extend-trial-btn-${r.id}`}>
                      <CalendarPlus className="mr-1 h-3.5 w-3.5" /> {t("x.extendTrial")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {stripe && (
        <div className="mt-10" data-testid="admin-stripe-catalog">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2"><Package className="h-5 w-5 text-cyan-dark" /><h2 className="font-heading text-xl font-extrabold text-navy">Stripe-Katalog</h2></div>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${stripe.stripe_mode === "live" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="stripe-mode">Modus: {stripe.stripe_mode}</span>
          </div>
          <p className="mt-1 text-sm text-slate-500">Produkt-/Preis-Konfiguration (read-only, ohne Secrets). {stripe.configured}/{stripe.count} konfiguriert.</p>
          <div className="mt-4 card-soft overflow-x-auto">
            <table className="w-full text-sm" data-testid="stripe-catalog-table">
              <thead><tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-400">
                <th className="px-4 py-3">Produkt</th><th className="px-4 py-3">Typ</th><th className="px-4 py-3">Lookup Key</th><th className="px-4 py-3">Preis</th><th className="px-4 py-3">Intervall</th><th className="px-4 py-3">Steuer</th><th className="px-4 py-3">Price ID</th><th className="px-4 py-3">Status</th>
              </tr></thead>
              <tbody>
                {stripe.products.map((p) => (
                  <tr key={p.lookup_key} className="border-b border-slate-50" data-testid={`stripe-row-${p.lookup_key}`}>
                    <td className="px-4 py-3 font-semibold text-navy">{p.internal_name}</td>
                    <td className="px-4 py-3 text-slate-500 capitalize">{p.kind}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{p.lookup_key}</td>
                    <td className="px-4 py-3">{p.unit_amount_eur != null ? `${p.unit_amount_eur.toLocaleString("de-DE")} ${p.currency}` : "—"}</td>
                    <td className="px-4 py-3 text-slate-500">{p.interval || "—"}</td>
                    <td className="px-4 py-3"><span className={p.tax_behavior === "exclusive" ? "text-emerald-600" : "text-amber-600"}>{p.tax_behavior || "—"}</span></td>
                    <td className="px-4 py-3 font-mono text-[11px] text-slate-400">{p.stripe_price_id || "—"}</td>
                    <td className="px-4 py-3">{p.configured
                      ? <span className="inline-flex items-center gap-1 text-emerald-600" data-testid={`stripe-ok-${p.lookup_key}`}><CheckCircle2 className="h-4 w-4" /> OK</span>
                      : <span className="inline-flex items-center gap-1 text-red-500" data-testid={`stripe-missing-${p.lookup_key}`}><AlertTriangle className="h-4 w-4" /> fehlt</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}


      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent data-testid="extend-trial-dialog">
          <DialogHeader><DialogTitle>{t("x.extendTrial")} — {target?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>{t("x.extendDays")}</Label>
            <Input type="number" min="1" max="365" value={days} onChange={(e) => setDays(e.target.value)} data-testid="extend-days-input" />
          </div>
          <DialogFooter>
            <Button onClick={extend} disabled={busy} data-testid="extend-confirm-btn" className="rounded-full bg-cyan text-white hover:bg-cyan-dark">{t("x.extend")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
